import { Button, Modal, TEOLoader, InputText, Checkbox } from '@teo/components';
import { Plus, Search, CrossFilled } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useGet } from '@/query';
import { PaginationAnything } from '@/components/DataTable/PaginationAnything';
import { SelectSearchDropdown } from '../SelectSearchDropdown';
import TextWithTooltip from '@/pages/Protected/Manage/Competencies/TextWithTooltip';
import { config } from '@/config';
import { axios } from '@/axios';
import { newUid } from '@/util';

const AddCompetenceProfileModal = ({
  closeModal,
  setIsOpenAddModal,
  setRefetchTable,
  authUser,
  setActiveProfile,
  activeProfile,
  categories,
}) => {
  const { t } = useTranslation();

  const [competenciesProfile, setCompetenciesProfile] = useState([]);

  const [category, setCategory] = useState({
    id: 0,
    name: t('pages.manage_competencies.all_categories'),
  });

  const [loadCompetencies, setLoadCompetencies] = useState(true);
  const [sort, setSort] = useState([{ id: 'competenceId', desc: false }]);

  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  const [deleteList, setDeleteList] = useState([]);
  const [addList, setAddList] = useState([]);

  const [mandatoryChanges, setMandatoryChanges] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);

  useGet(`/professioncompetencedetails`, {
    professionId: activeProfile.id,
    onSuccess: (result) => {
      setCompetenciesProfile(result?.data || []);
    },
  });

  const hendleDelete = (item) => {
    const existsAdd = addList?.find(
      (el) => el?.competenceId === item.competenceId
    );

    if (existsAdd) {
      setAddList((prev) =>
        prev?.filter((el) => el?.competenceId !== item.competenceId)
      );
    } else {
      setDeleteList((prev) => [...prev, item]);
    }
  };

  const hendleAdd = (item) => {
    const existsDelete = deleteList?.find(
      (el) => el?.competenceId === item.competenceId
    );

    if (existsDelete) {
      setDeleteList((prev) =>
        prev?.filter((el) => el?.competenceId !== item.competenceId)
      );
    } else {
      setAddList((prev) => [...prev, item]);
    }
  };

  const commonObjects = (arr1, arr2) => {
    const competenceIds = new Set(arr2.map((item) => item.competenceId));

    return arr1.filter((item) => competenceIds.has(item.competenceId));
  };

  const handleMandatory = (item, isCheck) => {
    const checked =
      typeof isCheck === 'boolean' ? isCheck : isCheck?.target?.checked;

    setMandatoryChanges((prev) => ({
      ...prev,
      [item.competenceId]: checked,
    }));
  };

  const hendleSave = async () => {
    try {
      if (addList?.length > 0) {
        await Promise.all(
          addList.map((el) =>
            axios.post(`${config.backend}/professioncompetences`, {
              professionId: activeProfile.id,
              competenceId: el?.competenceId,
              isMandatory:
                mandatoryChanges[el.competenceId] !== undefined
                  ? mandatoryChanges[el.competenceId]
                  : false,
            })
          )
        );
      }

      if (deleteList?.length > 0) {
        const result = commonObjects(deleteList, competenciesProfile);

        await Promise.all(
          result.map((el) =>
            axios.delete(`${config.backend}/professioncompetence/${el.id}`)
          )
        );
      }

      const mandatoryUpdates = competenciesProfile.filter((el) => {
        const hasChange = mandatoryChanges[el.competenceId] !== undefined;

        const isDeleted = deleteList.some(
          (item) => item.competenceId === el.competenceId
        );

        return hasChange && !isDeleted;
      });

      if (mandatoryUpdates.length > 0) {
        await Promise.all(
          mandatoryUpdates.map((el) =>
            axios.put(`${config.backend}/professioncompetence/${el.id}`, {
              isMandatory: mandatoryChanges[el.competenceId],
            })
          )
        );
      }

      setRefetchTable(newUid(20));
      setActiveProfile(null);
      document.body.style.overflow = 'unset';
      setIsOpenAddModal(false);
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  return (
    <div className="bg-white">
      <div className="fixed inset-0 z-[100] flex rounded-xl bg-white/75 px-3">
        <div className="mx-auto my-8 h-max w-full max-w-xl rounded-xl bg-white drop-shadow-m">
          <Modal.Header onClose={closeModal}>
            <h2 className="text-sm font-semibold">
              {t('pages.manage_competencies.edit_competence')}
            </h2>
          </Modal.Header>

          <Modal.Body>
            <div className="-ml-6 -mr-6 -mt-6 flex max-h-[65vh] flex-col overflow-y-auto border-b py-6">
              <div className="flex flex-col gap-1">
                <div className="px-6">
                  <h3 className="mb-3 text-lg">
                    {t('pages.manage_competencies.add_competence')}
                  </h3>
                </div>

                <div className={`relative mb-2 px-6`}>
                  <InputText
                    value={filter}
                    onChange={(ev) => setFilter(ev.target.value)}
                    Icon={Search}
                    placeholder={t('search.course')}
                  />
                </div>

                <div className="relative z-[10] max-w-[250px] px-6">
                  <SelectSearchDropdown
                    clearButtonLabel="clear"
                    onChange={async (cat) => {
                      setCategory(cat);
                    }}
                    buttonText={`${t(
                      'pages.create_edit_course.select_language'
                    )}`}
                    options={[{ id: 0, name: 'All' }, ...categories]}
                    searchable={true}
                    value={category?.name ? category : undefined}
                  >
                    {(option) => (
                      <span className="flex items-center">
                        {option.color && (
                          <span
                            style={{ backgroundColor: option.color }}
                            className="mr-2 h-2 w-2 rounded-full"
                          ></span>
                        )}

                        {option.name}
                      </span>
                    )}
                  </SelectSearchDropdown>
                </div>

                <div className="flex max-h-[50vh] min-h-[300px] w-full flex-col overflow-y-auto px-6">
                  {loadCompetencies && (
                    <div className="mx-auto flex justify-center py-20 px-10">
                      <TEOLoader
                        loaderSrc="/images/TEOloader.gif"
                        className="w-[100px]"
                      />
                    </div>
                  )}

                  <PaginationAnything
                    className={`mt-3 flex w-full flex-col gap-2 ${
                      loadCompetencies === true && ' hidden '
                    }`}
                    fetch={useGet.bind(null, '/organisationcompetencedetails', {
                      lang: authUser?.lang,
                      organisationId: `${authUser?.activeOrganisationId},OR,organisationId=null`,
                      disabled: false,
                      competenceCategoryId: category?.id,
                      onSuccess: (result) => {
                        setLoadCompetencies(false);
                      },
                    })}
                    empty={{
                      template: 2,
                      loadContent: loadCompetencies,
                      text: t('empty_tenplate.course_list'),
                    }}
                    defaultSort={sort}
                    page={0}
                    pageSize={10}
                    filter={debouncedFilter}
                    render={(comp) => {
                      const exists = competenciesProfile?.find(
                        (el) => el.competenceId === comp.competenceId
                      );

                      const newComp = addList?.find(
                        (el) => el.competenceId === comp.competenceId
                      );

                      const delComp = deleteList?.find(
                        (el) => el.competenceId === comp.competenceId
                      );

                      const isMandatory =
                        mandatoryChanges[comp.competenceId] !== undefined
                          ? mandatoryChanges[comp.competenceId]
                          : exists
                          ? exists?.isMandatory
                          : false;

                      return (
                        <div
                          key={comp?.competenceId}
                          className={`group flex w-full cursor-pointer flex-col gap-6 rounded-lg border sm:flex-row ${
                            ((exists && !delComp) || newComp) && 'bg-grey-01'
                          }`}
                        >
                          <div className="flex w-full items-center px-3 py-2 text-black">
                            {comp?.competenceCategoryColorCode && (
                              <span
                                style={{
                                  backgroundColor:
                                    comp?.competenceCategoryColorCode,
                                }}
                                className="mr-2 h-1.5 w-1.5 min-w-1.5 rounded-full"
                              ></span>
                            )}
                            {comp.name}
                            <div className="ml-auto flex items-center">
                              {((exists && !delComp) || newComp) && (
                                <div className="ml-2 flex items-center">
                                  <p className="pr-2 text-xs text-grey-08">
                                    {t('pages.create_courses.mandatory')}
                                  </p>

                                  <Checkbox
                                    checked={isMandatory}
                                    onChange={(isCheck) =>
                                      handleMandatory(comp, isCheck)
                                    }
                                  />
                                </div>
                              )}

                              <p className="mx-2 min-w-max text-xs text-grey-06">
                                {comp?.code}
                              </p>
                            </div>

                            <div>
                              {(exists && !delComp) || newComp ? (
                                <Button
                                  className="!px-2"
                                  variant="ghost"
                                  onClick={() => {
                                    const item = exists || newComp;

                                    hendleDelete(item);
                                  }}
                                >
                                  <CrossFilled className="h-4 w-4 text-grey-06" />
                                </Button>
                              ) : (
                                <Button
                                  className="!px-2"
                                  variant="ghost"
                                  onClick={() => hendleAdd(comp)}
                                >
                                  <Plus className="h-4 w-4 text-grey-06" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <Button
                variant="ghost"
                onClick={() => {
                  document.body.style.overflow = 'unset';
                  setIsOpenAddModal(false);
                }}
              >
                {t('button.cancel')}
              </Button>

              <Button onClick={() => hendleSave()}>
                {t('button.add_btn')}
              </Button>
            </div>
          </Modal.Body>
        </div>
      </div>
    </div>
  );
};

export default AddCompetenceProfileModal;
